import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { saveAs } from "file-saver";
import { LoadingController, ToastController } from "ionic-angular";
import * as moment from "moment-timezone";
import { AuthService } from "../../app/auth/auth.service";
import { ConfigurationService } from "../configuration/configuration.service";

@Injectable()
export class ReportExecutionService {
  private url: string;
  private spinner = null;
  private spinnerCounter = 0;
  private apiUrl: string;

  constructor(
    private authService: AuthService,
    private authSvc: AuthService,
    public loadingCtrl: LoadingController,
    public http: HttpClient,
    private config: ConfigurationService,
    public toastCtrl: ToastController,
    private translate: TranslateService
  ) {
    this.url = config.getConfig().reportExecutionUrl;
    this.apiUrl = config.getConfig().apiUrl;
  }

  /**
   * Downloads a report from the report execution service and optionally saves it to disk.
   * @param reportName The name of the report to execute
   * @param payload The report parameters and configuration
   * @param fileName The name to use when saving the file (default: "report.pdf")
   * @param mediaType The MIME type of the report (default: "application/pdf")
   * @param processInMemory If true, returns the blob without saving to disk or showing UI feedback (default: false)
   * @returns Promise that resolves with the report blob
   */
  public downloadReport(
    reportName: string,
    payload: any,
    fileName: string = "report.pdf",
    mediaType: string = "application/pdf",
    processInMemory: boolean = false
  ): Promise<Blob> {
    if (!processInMemory) {
      this.increaseSpinnerCount("Downloading Report");
    }
    return new Promise((resolve, reject) => {
      this.http
        .post(`${this.url}/reports/${reportName}`, payload, {
          responseType: "blob",
        })
        .subscribe(
          (response) => {
            const blob = new Blob([response], { type: mediaType });
            if (!processInMemory) {
              saveAs(blob, fileName);
              this.presentSuccessToast(
                this.translate.instant("successToDownloadReport")
              );
              this.decreaseSpinnerCount();
            }
            resolve(blob);
          },
          (e) => {
            console.error(e);
            if (!processInMemory) {
              this.presentErrorToast(
                e,
                this.translate.instant("failedToDownloadReport")
              );
              this.decreaseSpinnerCount();
            }
            reject(e);
          }
        );
    });
  }

  /**
   * Builds a standardized report filename with organization name and timestamp.
   * @param reportName The base name of the report
   * @param reportFormat The file extension/format (e.g., "pdf", "xlsx")
   * @returns Formatted filename in the pattern: reportName_orgName_timestamp.format
   */
  buildReportFilename(reportName: string, reportFormat: string) {
    const orgName = this.authSvc.getUser().tenantName;
    let now = moment(new Date()).format("YYYY_MM_DD_HH_mm_ss");

    return `${reportName}_${orgName.replace(/ /g, "_")}_${now}.${reportFormat}`;
  }

  /**
   * Increments the spinner counter and displays a loading spinner if not already shown.
   * @param message The message to display in the loading spinner (default: "Loading...")
   */
  public increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  /**
   * Decrements the spinner counter and dismisses the loading spinner when count reaches zero.
   */
  public decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /**
   * Displays an error toast notification with a 6-second duration.
   * @param message The error message to display
   */
  presentErrorToaster(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  /**
   * Displays a success toast notification with a 3-second duration.
   * @param message The success message to display
   */
  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  /**
   * Displays an error toast with contextual information based on the HTTP error response.
   * @param err The HTTP error response object
   * @param message The base error message to display (will be augmented with error details)
   */
  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        // Do not modify message
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  /* getQuicksightDashboardAPI(payload) {
    return this.http.post(`${this.apiUrl}/quicksight/${payload.parameters.tenant_id}`, payload);
  } */

  /**
   * Retrieves QuickSight dashboard URL for a registered user.
   * @param payload The request payload containing tenant_id and dashboard parameters
   * @returns Observable with the QuickSight dashboard API response
   */
  getQuicksightDashboardAPIForRegisteredUser(payload) {
    return this.http.post(
      `${this.apiUrl}/quicksight/user/${payload.parameters.tenant_id}`,
      payload
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/report-execution-service/report-execution.service.ts